import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { TextDisplayCard } from 'components/TextDisplayCard/TextDisplayCard'
import useModalClose from 'hooks/useModalClose'
import { CursorPointer, Flex, ItemsGrid, ModalScrollContainer, ThinSeparator } from 'styling/GlobalStyles'
import { BodyText, TitleText } from 'styling/Typography'
import { IInventoryRequestResponse } from 'types/api/inventoryRequestApi.interface'
import { formatDateForDisplay } from 'utils/helpers'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { IStatusPill } from 'types/ui/statusPill.interface'
import {
  getInventoryRequestStatusLabel,
  INVENTORY_REQUEST_STATUS_APPROVED,
} from './inventoryRequestsData'

const REQUEST_STATUS_TONES: { [status: number]: IStatusPill['status'] } = {
  1: 'warning',
  2: 'success',
  3: 'info',
  4: 'success',
  5: 'danger',
}

interface IViewInventoryRequestModalProps {
  closeModal: () => void
  value?: IInventoryRequestResponse
}

export const ViewInventoryRequestModal = ({ closeModal, value }: IViewInventoryRequestModalProps) => {
  useFocusFirstInput()
  useModalClose(closeModal)

  const requesterName = value?.requestedByUser
    ? `${value.requestedByUser.firstName} ${value.requestedByUser.lastName}`
    : '/'

  const approverName = value?.approvedByUser
    ? `${value.approvedByUser.firstName} ${value.approvedByUser.lastName}`
    : '/'

  return (
    <Modal black>
      <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
        <Flex center gap='0.75rem'>
          <TitleText weight='bold'>Request Preview</TitleText>
          {value?.status != null && (
            <StatusPill status={REQUEST_STATUS_TONES[value.status] || 'neutral'}>
              {getInventoryRequestStatusLabel(value.status)}
            </StatusPill>
          )}
        </Flex>
        <CursorPointer onClick={closeModal}>
          <CloseIcon />
        </CursorPointer>
      </Flex>
      <ModalScrollContainer>
        <ItemsGrid columns={2}>
          <TextDisplayCard width='100%' title={formatDateForDisplay(value?.createdAt)} labelText='Created At' />
          <TextDisplayCard width='100%' title={value?.department?.name || '/'} labelText='Department' />
          <TextDisplayCard width='100%' title={value?.costCenter?.name || '/'} labelText='Cost Center' />
          <TextDisplayCard width='100%' title={requesterName} labelText='Requested By' />
          <TextDisplayCard width='100%' title={approverName} labelText='Approved By' />
          <TextDisplayCard
            width='100%'
            title={value?.approvedAt ? formatDateForDisplay(value.approvedAt) : '/'}
            labelText='Approved At'
          />
        </ItemsGrid>

        {value?.status === INVENTORY_REQUEST_STATUS_APPROVED && (
          <Flex center gap='0.5rem' mt='8'>
            <StatusPill status='accent'>RESERVES STOCK</StatusPill>
          </Flex>
        )}

        <TextDisplayCard style={{ marginTop: '1rem' }} title={value?.notes || '/'} labelText='Notes' width='100%' />

        {value?.declineReason && (
          <TextDisplayCard
            style={{ marginTop: '1rem' }}
            title={value.declineReason}
            labelText='Decline Reason'
            width='100%'
          />
        )}

        <ThinSeparator />
        <Flex column gap='1rem'>
          <BodyText weight='bold'>Items:</BodyText>
          {value?.lines?.length ? (
            value.lines.map((l) => (
              <Flex key={l.id} width='100%' gap='1rem'>
                <TextDisplayCard
                  width='100%'
                  title={l.inventoryItem ? l.inventoryItem.name : '/'}
                  labelText='Item'
                />
                <TextDisplayCard width='100%' title={l.quantity} labelText='Quantity' />
              </Flex>
            ))
          ) : (
            <BodyText>No items</BodyText>
          )}
        </Flex>
      </ModalScrollContainer>
    </Modal>
  )
}
